import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut as firebaseSignOut, onAuthStateChanged, updateProfile } from 'firebase/auth';
import { doc, getDoc, setDoc, getDocs, collection } from 'firebase/firestore';
import { auth, googleProvider, db } from '../firebase';

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  isVenueOperator: boolean;
  isCoach: boolean;
  /** Klubtulajdonos (mobil-regisztráció role: 'club') vagy üzemeltető — a klub panelt látja */
  isClubOwner: boolean;
  userRole: 'admin' | 'venueOperator' | 'coach' | 'user';
  venueIds: string[];
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, displayName: string, username: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isVenueOperator, setIsVenueOperator] = useState(false);
  const [isCoach, setIsCoach] = useState(false);
  const [isClubOwner, setIsClubOwner] = useState(false);
  const [userRole, setUserRole] = useState<'admin' | 'venueOperator' | 'coach' | 'user'>('user');
  const [venueIds, setVenueIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const checkAdminStatus = async (uid: string, userObj?: User) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const isAdminUser = userData.isAdmin === true;
        const isVenueOp = userData.isVenueOperator === true;
        const isCoachUser = userData.isCoach === true;

        setIsAdmin(isAdminUser);
        setIsVenueOperator(isVenueOp);
        setIsCoach(isCoachUser);
        setIsClubOwner(userData.role === 'club' || isVenueOp);
        setVenueIds(userData.venueIds || []);

        if (isAdminUser) {
          setUserRole('admin');
        } else if (isVenueOp) {
          setUserRole('venueOperator');
        } else if (isCoachUser) {
          setUserRole('coach');
        } else {
          setUserRole('user');
        }
      } else {
        // New user - create document with all necessary fields
        console.log('Creating new user document for uid:', uid);
        const currentUser = userObj || auth.currentUser;
        console.log('User object:', currentUser);
        const isFirstUser = await isFirstUserInSystem();

        // Generate username from email (before @ symbol, lowercase)
        const emailUsername = currentUser?.email?.split('@')[0]?.toLowerCase() || 'user';

        const newUserData = {
          uid: uid,
          email: currentUser?.email || '',
          displayName: currentUser?.displayName || currentUser?.email?.split('@')[0] || 'User',
          username: emailUsername,
          isAdmin: isFirstUser,
          isVenueOperator: false,
          isCoach: false,
          role: isFirstUser ? 'admin' : 'user',
          createdAt: new Date().toISOString()
        };
        console.log('Creating user with data:', newUserData);
        await setDoc(doc(db, 'users', uid), newUserData);
        console.log('User document created successfully');
        setIsAdmin(isFirstUser);
        setIsVenueOperator(false);
        setIsCoach(false);
        setUserRole(isFirstUser ? 'admin' : 'user');
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
      setIsVenueOperator(false);
      setIsCoach(false);
      setUserRole('user');
    }
  };

  const isFirstUserInSystem = async (): Promise<boolean> => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      return usersSnapshot.empty;
    } catch (error) {
      console.error('Error checking if first user:', error);
      return false;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        await checkAdminStatus(user.uid);
      } else {
        setIsAdmin(false);
        setIsVenueOperator(false);
        setUserRole('user');
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await checkAdminStatus(result.user.uid, result.user);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      await checkAdminStatus(result.user.uid, result.user);
    } catch (error) {
      console.error('Error signing in with email:', error);
      throw error;
    }
  };

  const signUpWithEmail = async (email: string, password: string, displayName: string, username: string) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(result.user, { displayName });

      // Reload user to get updated profile
      await result.user.reload();
      const updatedUser = auth.currentUser;

      await checkAdminStatus(result.user.uid, updatedUser || result.user);

      // Update user document with display name and username
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      if (userDoc.exists()) {
        await setDoc(doc(db, 'users', result.user.uid), {
          ...userDoc.data(),
          displayName,
          username: username.toLowerCase(),
          email
        }, { merge: true });
      }
    } catch (error) {
      console.error('Error signing up with email:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setIsAdmin(false);
      setIsVenueOperator(false);
      setIsCoach(false);
      setIsClubOwner(false);
      setUserRole('user');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const value = {
    user,
    isAdmin,
    isVenueOperator,
    isCoach,
    isClubOwner,
    userRole,
    venueIds,
    loading,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    signOut
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
